import { useLocalStorage } from './useLocalStorage';

// Type defining the structure for filter values:
// a record where keys are strings and values are arrays of strings
type FilterValues = Record<string, string[]>;

/**
 * Custom React hook for managing filters stored in localStorage per page
 * @param page - the page identifier to create a unique storage key
 * @returns An object containing functions to get filter values,
 *          update filters, and the current filters state
 */
export default function useFilterLocalStorage(page: string) {
  // Generate a unique localStorage key for the filters on the given page
  const storageKey = `filters_${page}`;

  // Use the custom useLocalStorage hook to read/write filter values
  // initialized as an empty object if not present in localStorage
  const [filters, setFilters] = useLocalStorage<FilterValues>(storageKey, {});

  /**
   * Retrieves the current value array for a specific filter name.
   * Returns an empty array if the filter is not set.
   * @param filterName - the name of the filter to get values for
   */
  const getFilterValue = (filterName: string): string[] => filters[filterName] || [];

  /**
   * Updates the filter values for a specific filter name.
   * Merges the new values with the existing filters in state.
   * @param filterName - the name of the filter to update
   * @param values - array of string values to set for the filter
   */
  const updateFilter = (filterName: string, values: string[]) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: values,
    }));
  };

  // Return an object with the getter, updater, and the current filters state
  return { getFilterValue, updateFilter, filters };
}
